/**
 * phaseCapabilityTimeline — the chart-ready assembly of the phase-plan checks.
 *
 * The three phase engines each answer one question about ONE phase:
 * {@link assessPhaseFeasibility} whether the mix is big enough (N-0),
 * {@link assessPhaseContingency} whether it survives losing a block (N-1),
 * {@link assessPhaseLeadTime} whether the equipment can arrive. This module
 * assembles them ACROSS phases into the two series a reader can actually
 * see: capability against requirement over the plan, and the long-lead
 * order windows that gate each phase date.
 *
 * It computes nothing new. Every number here comes from one of the three
 * assessments, so the charts and the risk rows can never disagree.
 *
 * Pure and side-effect free; `now` is injected so the lead-time column is
 * deterministic and testable.
 */
import type { ProjectIntent, ProjectPhase } from '../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../types/siteRag';
import { assessPhaseFeasibility, firmCapacitySiteContextFromIntent } from './phaseFeasibility';
import {
  assessPhaseContingency,
  type PhaseBlockCounts,
  type SolvedScenarioPhaseContingency,
} from './phaseContingency';
import { assessPhaseLeadTime, type PhaseLeadTimeVerdict } from './phaseLeadTime';
import { effectivePhases } from './projectIntent';

export interface PhaseCapabilityPoint {
  phaseId: string;
  /** Short axis label: the phase label, or "Phase N" when unlabelled. */
  label: string;
  sequence: number;
  targetInServiceDate: string | null;
  /** Load the phase must serve (MW). */
  requiredMW: number;
  /** Load + planning reserve: the binding bar (MW). */
  requiredFirmMW: number;
  /** Firm capacity with everything running (MW). */
  firmN0MW: number;
  /** Firm capacity after losing the largest block (MW). */
  firmN1MW: number;
  /** firmN1MW - requiredFirmMW; negative is the shortfall. */
  n1MarginMW: number;
  /** True when N-0 passes and N-1 does not: the exposure worth drawing. */
  hiddenExposure: boolean;
  /** True when block counts were assumed rather than declared. */
  assumesWorstCase: boolean;
}

export interface PhaseCriticalPathBar {
  phaseId: string;
  /** Row label, e.g. "Phase 2 · Gas turbines". */
  label: string;
  sequence: number;
  /** Epoch ms of the latest defensible order date. */
  orderByMs: number;
  /** Epoch ms of the phase COD. */
  codMs: number;
  /** Lead-time window in months, for the tooltip. */
  leadTimeMonthsLow: number;
  leadTimeMonthsHigh: number;
  verdict: PhaseLeadTimeVerdict;
  /** True when the baseline is a nearest-class stand-in. */
  proxyBaseline: boolean;
}

export interface PhaseCapabilityTimeline {
  points: PhaseCapabilityPoint[];
  bars: PhaseCriticalPathBar[];
  /** Epoch ms of "now", the vertical marker on the critical-path chart. */
  nowMs: number;
  /** True when ANY point assumed block counts (drives the chart caveat). */
  anyWorstCase: boolean;
  /** True when ANY phase hides an N-1 exposure behind an N-0 pass. */
  anyHiddenExposure: boolean;
  /** Qualification note when a point derives from a modelled scenario (e.g. dual-feed grid-supply). */
  solvedQualificationNote?: string | null;
}

export interface PhaseCapabilityTimelineOptions {
  /** Declared block counts, e.g. from a promoted G&I scenario. */
  blockCounts?: PhaseBlockCounts;
  /** Authoritative solved N-1 result for the phase targeted by a promoted scenario. */
  solvedPhaseContingency?: SolvedScenarioPhaseContingency | null;
  now?: Date;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

const phaseLabel = (phase: ProjectPhase): string =>
  phase.label?.trim() ? phase.label.trim() : `Phase ${phase.sequence}`;

/**
 * Evenly spaced tick positions across a millisecond domain.
 *
 * ── Why this exists (2026-08-17) ────────────────────────────────────────────
 * Recharts picks ticks with `getNiceTickValues`, which hunts for HUMAN-ROUND
 * numbers. Epoch milliseconds have none: a domain of roughly 1.783e12 -> 1.872e12
 * (Jun 2026 -> Mar 2029) has no round interval inside it, so the algorithm falls
 * back to powers of ten anchored at ZERO and renders
 *   Jan 1970 · Nov 1985 · Sep 2001 · Jul 2017
 * — 15.8-year steps starting at the Unix epoch — while the actual bars are
 * squeezed into the last 4% of the plot. Live on the Redhead phase plan, whose
 * data is entirely sound (domain min 2026-06-30).
 *
 * The bug is width-dependent, which is why it never appeared in jsdom: at narrow
 * widths Recharts emits a single tick and the snap cannot be seen. Supplying
 * ticks explicitly removes the dependency on that algorithm altogether, so the
 * axis is deterministic at every width.
 *
 * Returns `count` positions INCLUSIVE of both bounds. A degenerate or non-finite
 * domain yields an empty array — the caller then omits `ticks` and lets Recharts
 * do whatever it likes with a domain that has no meaningful scale anyway.
 */
export function timeAxisTicks(minMs: number, maxMs: number, count = 5): number[] {
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return [];
  if (maxMs <= minMs || count < 2) return [];
  const step = (maxMs - minMs) / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.round(minMs + step * i));
}

/**
 * Build both chart series from an intent. Returns empty series (never null)
 * when the intent carries no assessable phase, so callers render nothing
 * rather than branching on undefined.
 */
export function buildPhaseCapabilityTimeline(
  intent: ProjectIntent | undefined | null,
  options: PhaseCapabilityTimelineOptions = {},
): PhaseCapabilityTimeline {
  const now = options.now ?? new Date();
  const empty: PhaseCapabilityTimeline = {
    points: [], bars: [], nowMs: now.getTime(), anyWorstCase: false, anyHiddenExposure: false,
  };
  if (!intent) return empty;

  const view = effectivePhases(intent);
  const feasibilityContext = firmCapacitySiteContextFromIntent(intent);
  const points: PhaseCapabilityPoint[] = [];
  const bars: PhaseCriticalPathBar[] = [];
  let solvedQualificationNote: string | null = null;

  for (const phase of view.phases) {
    const feasibility = assessPhaseFeasibility(phase, feasibilityContext);
    if (feasibility) {
      const candidateSolved =
        options.solvedPhaseContingency?.phaseId === phase.id
          ? options.solvedPhaseContingency
          : null;
      const solvedValid =
        candidateSolved != null
        && candidateSolved.screenVerdict !== 'indeterminate'
        && Number.isFinite(candidateSolved.normalNetMW)
        && Number.isFinite(candidateSolved.contingencyNetMW)
        && Number.isFinite(candidateSolved.reserveRequiredMW)
        && Number.isFinite(candidateSolved.reserveMarginMW)
        && candidateSolved.normalNetMW >= 0
        && candidateSolved.contingencyNetMW >= 0
        && candidateSolved.reserveRequiredMW > 0
        && Math.abs(candidateSolved.reserveRequiredMW - feasibility.requiredFirmMW) <= 0.5
        && (candidateSolved.screenVerdict !== 'pass'
          || (candidateSolved.contingencyNetMW + 1e-6 >= candidateSolved.reserveRequiredMW
            && candidateSolved.contingencyNetMW + 1e-6 >= feasibility.requiredFirmMW
            && candidateSolved.reserveMarginMW >= -1e-6));
      if (solvedValid && candidateSolved) {
        const firmN0MW = round1(candidateSolved.normalNetMW);
        const firmN1MW = round1(candidateSolved.contingencyNetMW);
        const requiredFirmMW = round1(feasibility.requiredFirmMW);
        const n1MarginMW = round1(candidateSolved.reserveMarginMW);
        const n1Adequate =
          candidateSolved.screenVerdict === 'pass'
          && candidateSolved.contingencyNetMW + 1e-6 >= candidateSolved.reserveRequiredMW
          && candidateSolved.contingencyNetMW + 1e-6 >= feasibility.requiredFirmMW
          && candidateSolved.reserveMarginMW >= -1e-6;
        if (candidateSolved.qualificationNote) {
          solvedQualificationNote = candidateSolved.qualificationNote;
        }
        points.push({
          phaseId: phase.id,
          label: phaseLabel(phase),
          sequence: phase.sequence,
          targetInServiceDate: phase.targetInServiceDate,
          requiredMW: round1(feasibility.requiredMW),
          requiredFirmMW,
          firmN0MW,
          firmN1MW,
          n1MarginMW,
          hiddenExposure: feasibility.firmAdequate && !n1Adequate,
          assumesWorstCase: false,
        });
      } else {
        const phaseBlockCounts =
          options.solvedPhaseContingency != null
          && options.solvedPhaseContingency.phaseId !== phase.id
            ? {}
            : (options.blockCounts ?? {});
        const n1 = assessPhaseContingency(feasibility, phaseBlockCounts);
        points.push({
          phaseId: phase.id,
          label: phaseLabel(phase),
          sequence: phase.sequence,
          targetInServiceDate: phase.targetInServiceDate,
          requiredMW: round1(feasibility.requiredMW),
          requiredFirmMW: round1(feasibility.requiredFirmMW),
          firmN0MW: round1(feasibility.firmDeliverableMW),
          firmN1MW: round1(n1.firmAfterN1MW),
          n1MarginMW: round1(n1.firmAfterN1MW - feasibility.requiredFirmMW),
          hiddenExposure: n1.hiddenExposure,
          assumesWorstCase: n1.assumesWorstCase,
        });
      }
    }

    const lead = assessPhaseLeadTime(phase, now);
    if (!lead || !lead.targetInServiceDate) continue;
    const codMs = Date.parse(lead.targetInServiceDate);
    if (!Number.isFinite(codMs)) continue;
    for (const component of lead.components) {
      if (!component.orderByIso) continue;
      const orderByMs = Date.parse(component.orderByIso);
      if (!Number.isFinite(orderByMs)) continue;
      bars.push({
        phaseId: phase.id,
        label: `${phaseLabel(phase)} · ${PHASE_POWER_COMPONENT_LABEL[component.component]}`,
        sequence: phase.sequence,
        orderByMs,
        codMs,
        leadTimeMonthsLow: component.leadTimeMonthsLow,
        leadTimeMonthsHigh: component.leadTimeMonthsHigh,
        verdict: component.verdict,
        proxyBaseline: component.proxyBaseline,
      });
    }
  }

  // Earliest order window first: the reader's eye should land on what is
  // already late, not on whatever phase happens to be sequenced first.
  bars.sort((a, b) => a.orderByMs - b.orderByMs || a.sequence - b.sequence);

  return {
    points,
    bars,
    nowMs: now.getTime(),
    anyWorstCase: points.some((p) => p.assumesWorstCase),
    anyHiddenExposure: points.some((p) => p.hiddenExposure),
    ...(solvedQualificationNote ? { solvedQualificationNote } : {}),
  };
}
